import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Chip
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  AddTestPointButton,
  DeleteTestPointButton,
  PassFailChip
} from '../shared';
import type { BasicReadingTestPoint } from './types';

type Props = {
  testPoints: BasicReadingTestPoint[];
  onAddTestPoint: () => void;
  onDeleteTestPoint: (id: string) => void;
  onUpdateTestPoint: (
    id: string,
    field: 'asFound' | 'asLeft',
    value: number | null
  ) => void;
  isReadOnly?: boolean;
};

const BasicReadingForm: React.FC<Props> = ({
  testPoints,
  onAddTestPoint,
  onDeleteTestPoint,
  onUpdateTestPoint,
  isReadOnly = false
}) => {
  const { t } = useTranslation('calibration');

  return (
    <Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('basicReading.columnNominal')}</TableCell>
            <TableCell>{t('basicReading.columnTolerance')}</TableCell>
            <TableCell>{t('basicReading.columnAsFound')}</TableCell>
            <TableCell>{t('basicReading.columnAsFoundResult')}</TableCell>
            <TableCell>{t('basicReading.columnAsLeft')}</TableCell>
            <TableCell>{t('basicReading.columnAsLeftResult')}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {testPoints.map(point => {
            const asFoundError =
              point.asFound !== null
                ? Number((point.asFound - point.nominalValue).toFixed(10))
                : null;
            const asLeftError =
              point.asLeft !== null
                ? Number((point.asLeft - point.nominalValue).toFixed(10))
                : null;

            return (
              <TableRow key={point.id}>
                <TableCell>{point.nominalValue}</TableCell>
                <TableCell>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    {point.tolerance}
                    {point.noCatalogBacking && (
                      <Tooltip title="Manual tolerance — no catalog range backs this point, so it is not verified against equipment specs.">
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          icon={<WarningAmberIcon />}
                          label="No catalog backing"
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    disabled={isReadOnly}
                    inputProps={{ inputMode: 'decimal' }}
                    value={point.asFound !== null ? String(point.asFound) : ''}
                    onChange={e => {
                      const raw = e.target.value;
                      onUpdateTestPoint(
                        point.id,
                        'asFound',
                        raw === '' ? null : Number(raw)
                      );
                    }}
                    sx={{ width: 120 }}
                  />
                </TableCell>
                <TableCell>
                  <PassFailChip
                    error={asFoundError}
                    tolerance={point.tolerance}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    disabled={isReadOnly}
                    inputProps={{ inputMode: 'decimal' }}
                    value={point.asLeft !== null ? String(point.asLeft) : ''}
                    onChange={e => {
                      const raw = e.target.value;
                      onUpdateTestPoint(
                        point.id,
                        'asLeft',
                        raw === '' ? null : Number(raw)
                      );
                    }}
                    sx={{ width: 120 }}
                  />
                </TableCell>
                <TableCell>
                  <PassFailChip
                    error={asLeftError}
                    tolerance={point.tolerance}
                  />
                </TableCell>
                <TableCell>
                  <DeleteTestPointButton
                    onClick={() => onDeleteTestPoint(point.id)}
                    disabled={isReadOnly}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {!isReadOnly && (
        <Box sx={{ mt: 1, ml: 1 }}>
          <AddTestPointButton onClick={onAddTestPoint} />
        </Box>
      )}
    </Box>
  );
};

export default BasicReadingForm;
